import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Play, Youtube, Video, Info, Save } from "lucide-react";

// Convert user-friendly video URLs into embed-friendly URLs so the iframe
// actually plays instead of showing "refused to connect".
//   • youtube.com/watch?v=X       → youtube.com/embed/X
//   • youtu.be/X                  → youtube.com/embed/X
//   • loom.com/share/X            → loom.com/embed/X
//   • vimeo.com/X                 → player.vimeo.com/video/X
//   • Anything else               → passed through as-is (already an embed
//                                    URL or a direct MP4/WebM link).
const toEmbedUrl = (raw) => {
    if (!raw) return "";
    const url = raw.trim();
    try {
        const u = new URL(url);
        // YouTube
        if (u.hostname.includes("youtube.com") && u.pathname === "/watch") {
            const id = u.searchParams.get("v");
            if (id) return `https://www.youtube.com/embed/${id}`;
        }
        if (u.hostname === "youtu.be") {
            const id = u.pathname.slice(1);
            if (id) return `https://www.youtube.com/embed/${id}`;
        }
        // Loom
        if (u.hostname.includes("loom.com") && u.pathname.startsWith("/share/")) {
            return url.replace("/share/", "/embed/");
        }
        // Vimeo
        if (u.hostname === "vimeo.com") {
            const id = u.pathname.split("/").filter(Boolean)[0];
            if (id) return `https://player.vimeo.com/video/${id}`;
        }
    } catch { /* not a URL — probably a data URL / just return */ }
    return url;
};

const isDirectVideo = (url) => /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url || "");

/**
 * TutorialModal — plays a saved tutorial video for a given key
 * (e.g. `pas_create`, `roa_create`). Super-admins can also paste/save a
 * URL right from the modal so onboarding videos can be updated without a
 * trip to Control Panel.
 */
const TutorialModal = ({ open, onClose, tutorialKey, title, description, user }) => {
    const [videoUrl, setVideoUrl] = useState("");
    const [loading, setLoading] = useState(true);
    const [savingUrl, setSavingUrl] = useState(false);
    const [draftUrl, setDraftUrl] = useState("");
    const [editing, setEditing] = useState(false);
    const isSuperAdmin = user?.role === "super_admin";

    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                setLoading(true);
                const r = await api.get("/settings");
                const url = ((r.data || {}).tutorial_videos || {})[tutorialKey] || "";
                setVideoUrl(url);
                setDraftUrl(url);
                setEditing(false);
            } catch {
                setVideoUrl("");
            } finally { setLoading(false); }
        })();
    }, [open, tutorialKey]);

    const embedUrl = useMemo(() => toEmbedUrl(videoUrl), [videoUrl]);

    const saveUrl = async () => {
        setSavingUrl(true);
        try {
            const cur = (await api.get("/settings")).data || {};
            const nextMap = { ...(cur.tutorial_videos || {}), [tutorialKey]: draftUrl.trim() };
            await api.put("/settings", { tutorial_videos: nextMap });
            setVideoUrl(draftUrl.trim());
            setEditing(false);
            toast.success("Tutorial video saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save the video URL");
        } finally { setSavingUrl(false); }
    };

    if (!open) return null;

    return createPortal(
        <div className="fixed inset-0 z-[110] bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={onClose}
             data-testid={`tutorial-modal-${tutorialKey}`}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden"
                 onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between gap-3 px-6 pt-5 pb-3 border-b border-slate-100">
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.24em] text-[#25908A] font-bold flex items-center gap-1.5">
                            <Play className="h-3 w-3" /> Show me how
                        </div>
                        <h2 className="font-heading text-xl font-bold text-slate-900 mt-0.5">{title}</h2>
                        {description && <p className="text-sm text-slate-500 mt-1">{description}</p>}
                    </div>
                    <button
                        onClick={onClose}
                        aria-label="Close tutorial"
                        data-testid="tutorial-close-btn"
                        className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="px-6 py-5 space-y-4">
                    {loading ? (
                        <div className="aspect-video bg-slate-100 rounded-xl animate-pulse" />
                    ) : videoUrl && !editing ? (
                        <div className="rounded-xl overflow-hidden bg-slate-900 aspect-video">
                            {isDirectVideo(embedUrl) ? (
                                <video
                                    src={embedUrl}
                                    controls
                                    className="w-full h-full"
                                    data-testid={`tutorial-video-${tutorialKey}`}
                                />
                            ) : (
                                <iframe
                                    src={embedUrl}
                                    title={title}
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    className="w-full h-full"
                                    data-testid={`tutorial-video-${tutorialKey}`}
                                />
                            )}
                        </div>
                    ) : (
                        <div className="rounded-xl border-2 border-dashed border-teal-300 bg-teal-50/40 p-8 text-center">
                            <Video className="h-9 w-9 mx-auto text-[#2EB4AC]" />
                            <p className="mt-3 font-heading font-semibold text-slate-900">
                                {videoUrl && editing ? "Update the tutorial video" : "No tutorial recorded yet"}
                            </p>
                            <p className="text-slate-500 text-sm mt-1 max-w-md mx-auto">
                                {isSuperAdmin
                                    ? "Paste a YouTube, Loom, or Vimeo share URL below and hit Save. Your team will see this walkthrough the next time they open this page."
                                    : "Your admin hasn't uploaded a tutorial video for this flow yet."}
                            </p>
                        </div>
                    )}

                    {isSuperAdmin && (
                        <div className="rounded-lg border border-slate-100 bg-slate-50/60 px-4 py-3 space-y-2">
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1.5">
                                <Youtube className="h-3 w-3" /> Admin · tutorial video URL
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="url"
                                    value={draftUrl}
                                    onChange={(e) => { setDraftUrl(e.target.value); setEditing(true); }}
                                    placeholder="https://youtu.be/… or loom.com/share/…"
                                    data-testid={`tutorial-url-input-${tutorialKey}`}
                                    className="flex-1 min-w-0 h-9 px-3 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#2EB4AC]/25 focus:border-[#2EB4AC] outline-none"
                                />
                                <button
                                    type="button"
                                    onClick={saveUrl}
                                    disabled={savingUrl || draftUrl.trim() === videoUrl.trim()}
                                    data-testid={`tutorial-url-save-${tutorialKey}`}
                                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition"
                                >
                                    <Save className="h-3.5 w-3.5" /> Save
                                </button>
                            </div>
                            <p className="text-[10px] text-slate-400 flex items-start gap-1"><Info className="h-3 w-3 shrink-0 mt-0.5" /> Paste a share URL — we auto-convert YouTube / Loom / Vimeo to their embed form.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

// Tiny helper button that opens a TutorialModal for a given key. Also
// auto-opens the modal on first ever visit via localStorage.
export const TutorialButton = ({
    tutorialKey,
    title,
    description,
    user,
    className = "",
    autoShowOnFirstVisit = true,
}) => {
    const [open, setOpen] = useState(false);
    useEffect(() => {
        if (!autoShowOnFirstVisit) return;
        const key = `odyssey_tutorial_${tutorialKey}_seen`;
        try {
            if (!localStorage.getItem(key)) {
                setOpen(true);
                localStorage.setItem(key, "1");
            }
        } catch { /* localStorage disabled — no auto-show, no crash */ }
    }, [tutorialKey, autoShowOnFirstVisit]);

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                data-testid={`tutorial-open-${tutorialKey}`}
                title="Show me how"
                className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-slate-200 hover:border-[#2EB4AC] hover:text-[#25908A] text-sm font-semibold text-slate-600 transition-colors ${className}`}
            >
                <Play className="h-3.5 w-3.5" /> Show me how
            </button>
            <TutorialModal
                open={open}
                onClose={() => setOpen(false)}
                tutorialKey={tutorialKey}
                title={title}
                description={description}
                user={user}
            />
        </>
    );
};

export default TutorialModal;
